import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Stack,
  Tooltip,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useSnackbar } from "notistack";
import {
  NOTISTACK_FAILURE_OPTIONS,
  NOTISTACK_SUCCESS_OPTIONS,
} from "constants/index";
import { useResetStrategyReportCacheMutation } from "features/settings/reportCache";

export const RESET_SR_CACHE_HINT =
  "Reset the cached calculations for the Strategy Report. Useful for removing stale data whenever the calculation logic changes";

/** Settings > Cache. */
const ReportCache = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resetCache, { isLoading }] = useResetStrategyReportCacheMutation();

  const handleClose = () => setConfirmOpen(false);

  const handleConfirm = async () => {
    try {
      await resetCache().unwrap();
      enqueueSnackbar("Strategy Report cache reset.", NOTISTACK_SUCCESS_OPTIONS);
      handleClose();
    } catch {
      enqueueSnackbar(
        "Failed to reset the Strategy Report cache.",
        NOTISTACK_FAILURE_OPTIONS,
      );
    }
  };

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Tooltip title={RESET_SR_CACHE_HINT} arrow>
          <IconButton size="small" aria-label="About Reset SR Cache">
            <InfoOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Button variant="outlined" onClick={() => setConfirmOpen(true)}>
          Reset SR Cache
        </Button>
      </Stack>
      <Dialog
        open={confirmOpen}
        onClose={isLoading ? undefined : handleClose}
        aria-labelledby="reset-sr-cache-title"
      >
        <DialogTitle id="reset-sr-cache-title">
          Reset the Strategy Report cache?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Every Strategy Report, for every account, will be recalculated the
            next time it is opened, which can take a few minutes.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            color="error"
            variant="contained"
            disabled={isLoading}
          >
            Reset
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReportCache;
